/**
 * @file src/room/roomStore.js
 * @description The one holder of live room state — phase, own role, the grant map, and the peer
 * list — exposed to the rest of the app as a module singleton with a read seam + subscription
 * (the same no-window-globals pattern the shared clock uses). The room client is the only writer.
 *
 * Wire voice ids are positional ("0"…"N-1" in roster order); the store owns the translation
 * between them and the runtime voice ids the registry uses, so everything outside this module
 * can ask questions in runtime ids and the wire never sees them.
 */

import { roomScore } from './roomScore.js';

/** @typedef {'idle'|'gathering'|'playing'} RoomPhase */

/**
 * @typedef {Record<string, Record<string, object>>} GrantMap
 * Who may drive what: WIRE voice id → holder clientId → that holder's grant detail.
 *
 * A voice can have MORE THAN ONE holder — two people driving one orbiter together is a supported
 * state, not an error. Which of them wins a simultaneous grab on the same control is decided
 * elsewhere, from the ordered event stream; it is deliberately not stored here, because room state
 * is a slow, replicated thing and a hand on a knob is not.
 *
 * The per-holder value is an OBJECT rather than `true` so that narrowing a grant later (to a named
 * set of controls, say) is a change to the data a room is given, not a change to this shape.
 * Nothing writes a field into it yet.
 */

export class RoomStore {
  #state = RoomStore.#initialState();
  #listeners = new Set();
  // wire id ("0"…"N-1") ↔ runtime voice id, seeded from the roster at room init.
  #wireToVoice = new Map();
  #voiceToWire = new Map();
  // The order those maps were built from, so re-seeding the same one is not announced as a change.
  #voiceOrderKey = null;

  static #initialState() {
    return {
      roomId: null,
      /** @type {RoomPhase} */
      phase: 'idle',
      /** @type {'creator'|'player'|'spectator'|null} */
      role: null,
      /** @type {GrantMap} who may drive which voice (empty until the room starts). */
      grants: {},
      /** This client's own id. `peers` is the relay's view of the OTHERS, so anything asking "who
       *  is connected" has to add us back in — and only the room client knows which one we are. */
      selfClientId: null,
      /** The relay's view of the OTHER members. `userId` is the peer's platform account id when
       *  they are signed in (guests have none) — it is what lets a surface look the person up
       *  against the public user card. Self-asserted at register and relayed verbatim, so it is
       *  display metadata, never proof of identity.
       *  @type {Array<{clientId: string, displayName?: string, userId?: string|null, role?: string}>} */
      peers: [],
      /** Server-minted session epoch (ms) — anchors `st` on outgoing envelopes. */
      sessionEpoch: null,
      /** The room's SHAPE as the relay states it on join: how many turntables the room deals onto
       *  and how many people hold a seat. The relay is the authority — the boot URL carries the
       *  same numbers and anyone can edit them, so two people reading their own links could
       *  otherwise render different rooms. Null until `room:joined` says otherwise.
       *  @type {{ voices: number, players: number }|null} */
      shape: null,
      /** How this room begins, as the RELAY states it on join. True: nobody presses
       *  Start — each seated player marks themselves ready and the room starts itself once they
       *  all have. False: the older rule, where the creator presses Start.
       *
       *  Read from the relay rather than from a build-time constant here on purpose: two clients on
       *  two versions would otherwise draw different affordances while sitting in the same room,
       *  and only one of them would match what the relay actually honours. Defaults to false so a
       *  relay too old to say behaves exactly as it did before. */
      readyGate: false,
      /** How many seats must be filled before the ready gate will let a room start. 1 with the gate
       *  off, which is the same as no floor at all. */
      minPlayers: 1,
      /** Whether THIS client has said it is ready. Never set optimistically: it is written when the
       *  relay echoes the change back, so what the button shows is what the room believes. */
      ready: false,
      /** Why this client holds no seat, when it holds none. `'account-seated'` means this account is
       *  already sitting in this room somewhere else — another tab, another device. Null covers the
       *  ordinary case (the seats are simply taken) and anyone who does hold a seat.
       *
       *  Worth its own field because the two situations render the same otherwise, and the duplicate
       *  one reads as a broken room: you can see an empty chair and you have not been given it.
       *  @type {'account-seated'|null} */
      listenerReason: null,
      /** How this room deals its CARDS, as the relay states it on join: `{ enabled, composition }`,
       *  or null for a room that deals the whole collection. Same provenance and same reason as
       *  `shape` above — the creator read it off the grant and stated it at mint, and the relay
       *  replays it to everyone, because a link-joiner has no way to ask the backend about a grant
       *  it only holds an id for. Every participant must deal the SAME hand, so all of them read
       *  this one answer rather than each computing their own.
       *  @type {{ enabled: boolean, composition: (Record<string, number>|null) }|null} */
      draw: null,
      /** The PINNED entities the hand is dealt from — `[{ id, kind }]`, the grant's own list, in the
       *  same one answer for everyone and for the same reason. Deliberately not the collection read's
       *  roster, which re-filters by release readiness: an entity archived mid-room would shorten one
       *  player's pool and not the other's, and the two would deal different hands.
       *  @type {Array<{ id: string, kind: string }>} */
      pinned: [],
      /** Wire ids of decks currently ARMING (loaded locally, waiting on the load-ready barrier). UI
       *  reflects these as "loading, not yet playable". Empty in the normal (settled) case. */
      armingDecks: [],
      /** A terminal room failure (e.g. a create/join that never seated) — null in the normal case. */
      error: null,
    };
  }

  get state() {
    return this.#state;
  }

  /** True once a room join is underway/complete for this tab. */
  get isActive() {
    return this.#state.roomId !== null;
  }

  /**
   * Seed the positional wire-id mapping from the ordered roster (runtime voice ids in order).
   *
   * This IS a change of room state — which deck a voice is on is what half the readers here are
   * keyed on — so it notifies like any other. Silently re-seeding the maps left anything holding a
   * voice-shaped question (who may drive this deck) answering for the deck that voice used to be
   * on, until some unrelated room event happened to arrive.
   *
   * @returns {boolean} whether the mapping actually changed — a load or a clear re-seeds the same
   * order more often than not, and the callers that republish on a change ask this rather than
   * keeping a second copy of it.
   */
  setVoiceOrder(voiceIds) {
    const order = Array.isArray(voiceIds) ? voiceIds : [];
    const key = order.map((id) => id ?? '').join('\u0000');
    if (key === this.#voiceOrderKey) return false;
    this.#voiceOrderKey = key;
    this.#wireToVoice.clear();
    this.#voiceToWire.clear();
    order.forEach((voiceId, index) => {
      if (typeof voiceId !== 'string' || voiceId.length === 0) return;
      const wireId = String(index);
      this.#wireToVoice.set(wireId, voiceId);
      this.#voiceToWire.set(voiceId, wireId);
    });
    this.#notify();
    return true;
  }

  /** @returns {string|null} the runtime voice id for a wire id, or null when unmapped. */
  voiceIdForWire(wireId) {
    return this.#wireToVoice.get(String(wireId)) ?? null;
  }

  /** @returns {string|null} the wire id for a runtime voice id, or null when unmapped. */
  wireIdForVoice(voiceId) {
    return this.#voiceToWire.get(voiceId) ?? null;
  }

  /**
   * May `clientId` drive `voiceId` (runtime id) right now?
   * - No room → true (the store gates nothing outside room mode).
   * - Gathering → false (the lobby is locked; the session hasn't started).
   * - Playing → only a holder of that voice (spectators hold nothing → never).
   *
   * `paramName` names the control being driven, and nothing reads it yet: a grant currently covers a
   * whole voice. It is in the signature from the start so that narrowing a grant to particular
   * controls stays a change to the grant data and to this one method, instead of a change to the
   * ten-odd gates that call it. Only the parameter write gate has a control name to pass; the
   * bridges (transport, loop, warp, sync, grid) gate whole-voice actions and pass nothing.
   */
  canEditVoice(voiceId, clientId, paramName = null) { // eslint-disable-line no-unused-vars
    if (!this.isActive) return true;
    if (this.#state.phase !== 'playing') return false;
    const wireId = this.wireIdForVoice(voiceId);
    if (wireId === null) return false;
    return this.#holds(wireId, clientId);
  }

  /**
   * May THIS client drive `voiceId` right now? The self-relative form of `canEditVoice`.
   *
   * Every gate so far has been a `canControl` thunk the room client hands down when it attaches to
   * something — an engine, a deck, a parameter manager — because the client is the only thing that
   * knows which member we are. That works for anything the client can attach to. It does not work
   * for the INPUT DEVICES: there is one keyboard, one MIDI port and one phone per person, they are
   * singletons that outlive every deck, and they belong to no deck in particular. They need to ask
   * the question themselves, and this is the form they can ask it in.
   *
   * Outside a room it answers true, exactly as `canEditVoice` does.
   */
  canSelfDrive(voiceId, paramName = null) {
    return this.canEditVoice(voiceId, this.#state.selfClientId, paramName);
  }

  /**
   * Does `clientId` hold this WIRE id (= deck/stage index) right now? Gates a deck LOAD, which can
   * target an EMPTY deck — one with no runtime voice yet, so `canEditVoice` (keyed on a runtime id)
   * can't answer. Loads only happen while playing; the lobby is locked.
   */
  ownsWire(wireId, clientId) {
    if (!this.isActive || this.#state.phase !== 'playing') return false;
    return this.#holds(wireId, clientId);
  }

  /** The runtime voice ids `clientId` currently holds. */
  ownedVoiceIds(clientId) {
    if (!this.isActive || this.#state.phase !== 'playing') return [];
    return Object.entries(this.currentGrants())
      .filter(([, holders]) => this.#isHolder(holders, clientId))
      .map(([wireId]) => this.voiceIdForWire(wireId))
      .filter((id) => id !== null);
  }

  /**
   * Which single holder speaks for this voice, or null when nobody holds it.
   *
   * Some jobs need exactly one client to do them even though several may drive the voice —
   * publishing the shared snapshot is the one that matters, because the relay keeps only the last
   * copy and a late joiner materializes from it. "The owner does it" used to name one client; now
   * it names all of them, and having every client publish the same voice means they overwrite each
   * other with genuinely different pictures (the projection inputs are resolved per USER).
   *
   * So pick one, the same way on every client: holders sorted, indexed by the voice. No election,
   * no message, no host — everyone already has the grant map, so everyone computes the same answer,
   * and it re-derives by itself when someone joins or leaves. Spreading by voice index rather than
   * always taking the first also keeps one client from publishing the whole room.
   */
  speakerForWire(wireId) {
    const holders = Object.keys(this.currentGrants()[String(wireId)] ?? {}).sort();
    if (holders.length === 0) return null;
    // Only a holder who is actually CONNECTED can speak. A member keeps their grants through the
    // disconnect grace window, so without this the voices that fell to someone who just closed
    // their tab would have no publisher at all until their seat expired — and a late joiner in that
    // window would materialize whatever was stored before they left. `peers` is the relay's view of
    // the others and drops a member the moment they detach, so this re-derives with it.
    const connected = this.connectedClientIds();
    // Nobody connected holds it → keep a stable answer rather than none; no one can publish either way.
    const eligible = holders.filter((id) => connected.has(id));
    const pool = eligible.length > 0 ? eligible : holders;
    const index = Number(wireId);
    return pool[Number.isFinite(index) ? Math.abs(Math.trunc(index)) % pool.length : 0];
  }

  /**
   * Everyone whose socket is attached right now — the peers the relay reports as connected, plus
   * ourselves (`peers` is its view of the OTHERS, so we are never in it). A member keeps their seat
   * and their grants through the disconnect grace window, so "is a member" and "is here" are
   * different questions, and anything picking someone to do work must ask the second one.
   */
  connectedClientIds() {
    const ids = new Set();
    for (const peer of this.#state.peers) {
      if (peer?.clientId && peer.connected !== false) ids.add(peer.clientId);
    }
    if (this.#state.selfClientId) ids.add(this.#state.selfClientId);
    return ids;
  }

  /** Does `clientId` speak for this voice? (Sole holder, or the one this voice picked.) */
  speaksForWire(wireId, clientId) {
    return !!clientId && this.speakerForWire(wireId) === clientId;
  }

  /**
   * Everyone in the room by clientId, this client first, then the others in the order the relay
   * lists them. ONE order, so a person keeps the same place in every stack of faces the room draws
   * — a deck whose holders happen to hash differently must not reorder the faces under it.
   *
   * `peers` is the relay's view of the OTHERS, so we are never in it and have to be added back.
   */
  memberOrder() {
    const ids = [];
    if (this.#state.selfClientId) ids.push(this.#state.selfClientId);
    for (const peer of this.#state.peers) {
      if (peer?.clientId && peer.clientId !== this.#state.selfClientId) ids.push(peer.clientId);
    }
    return ids;
  }

  /**
   * Who may drive this WIRE id right now, in room order.
   *
   * This is the GRANT — who is allowed to play the deck at all — not who has their hand on a
   * control this second; that is a lease and lives elsewhere. A deck nobody holds is absent from
   * the table rather than present and empty, so an empty answer is a real, correct state.
   */
  holdersOfWire(wireId) {
    if (!this.isActive || this.#state.phase !== 'playing') return [];
    const holders = this.currentGrants()[String(wireId)];
    if (!holders) return [];
    return this.memberOrder().filter((clientId) => this.#isHolder(holders, clientId));
  }

  /** Is `clientId` among the holders of this wire id? */
  #holds(wireId, clientId) {
    return this.#isHolder(this.currentGrants()[String(wireId)], clientId);
  }

  /** Grants derived from the score at relay-now, or the ordinary room map when no score is active. */
  currentGrants() {
    return roomScore.grants() ?? this.#state.grants;
  }

  /**
   * A holder entry counts only when the client is actually a key. `undefined` for an ungranted
   * client is the normal miss; the explicit own-property test is what stops a clientId that happens
   * to collide with something on `Object.prototype` from reading as a grant.
   */
  #isHolder(holders, clientId) {
    if (!holders || typeof clientId !== 'string' || clientId.length === 0) return false;
    return Object.prototype.hasOwnProperty.call(holders, clientId);
  }

  /** Merge a partial update and notify. The room client is the only caller. */
  update(patch) {
    this.#state = { ...this.#state, ...patch };
    this.#notify();
  }

  /** Notify readers when derived score state changed without replacing the stored room snapshot. */
  refresh() {
    this.#notify();
  }

  /** Add/replace one peer by clientId. */
  upsertPeer(peer) {
    if (!peer?.clientId) return;
    const peers = this.#state.peers.filter((p) => p.clientId !== peer.clientId);
    peers.push(peer);
    this.update({ peers });
  }

  /** Drop one peer by clientId. */
  /**
   * Drop every peer's ready flag.
   *
   * The relay clears readiness on its own members when the room starts, but says so by NOT sending
   * anything — `room:started` carries grants, not peer summaries — so a client left holding
   * `ready: true` for everyone it was waiting on. Nothing renders that today (the waiting room
   * unmounts the moment the phase leaves `gathering`), which is exactly why it would have gone
   * unnoticed until something new read the peer list and quietly disagreed with the room.
   */
  clearPeerReady() {
    if (!this.#state.peers.some((peer) => peer.ready)) return;
    this.update({ peers: this.#state.peers.map((peer) => ({ ...peer, ready: false })) });
  }

  removePeer(clientId) {
    if (!clientId) return;
    this.update({ peers: this.#state.peers.filter((p) => p.clientId !== clientId) });
  }

  /** Back to the initial state (room teardown / tests). */
  reset() {
    this.#state = RoomStore.#initialState();
    this.#wireToVoice.clear();
    this.#voiceToWire.clear();
    this.#voiceOrderKey = null;
    this.#notify();
  }

  /** Subscribe to any state change. Returns an unsubscribe fn. */
  subscribe(fn) {
    if (typeof fn !== 'function') return () => {};
    this.#listeners.add(fn);
    return () => this.#listeners.delete(fn);
  }

  #notify() {
    for (const fn of this.#listeners) {
      try {
        fn(this.#state);
      } catch (error) {
        console.warn('[room] store listener failed', error);
      }
    }
  }
}

/** The realm's one room store (idle outside room mode — every gate then answers "allowed"). */
export const roomStore = new RoomStore();
